import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { Formik, Form, FormikHelpers } from 'formik';
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValueLoadable
} from 'recoil';
import { Box } from '@mui/system';
import {
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  LinearProgress
} from '@material-ui/core';
import { useStyles } from './SmsConsentModalStyles';
import { getProfile, updateProfile } from 'src/Recoil/Selectors/Auth';
import { updateUser } from 'src/Recoil/Atoms/Login';
import { marginStyles } from 'src/Common/Styles/Margins';
import { Toast } from 'src/Components/Toasts';

interface FormValues {
  agreed_sms: string;
}

interface ToastState {
  message: string;
  type: 'success' | 'error';
  open: boolean;
}

interface ProfileData {
  agreed_sms: string | null;
  send_receive_sms: string | null;
  agreed_sms_date?: string;
  [key: string]: any;
}

const validationSchema = Yup.object().shape({
  agreed_sms: Yup.string().required('Required')
});

const REMINDER_KEY = 'sms_consent_reminder_time';

export default function SmsConsentModal() {
  const classes = useStyles();
  const marginClasses = marginStyles();
  const [open, setOpen] = useState(true);
  const [userAtom, setUserAtom] = useRecoilState(updateUser);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    message: '',
    type: 'success',
    open: false
  });
  const profileRequest = useRecoilValueLoadable(getProfile);
  const profile = profileRequest?.contents?.result as ProfileData || null;
  
  useEffect(() => {
    const reminderTime = localStorage.getItem(REMINDER_KEY);
    if (reminderTime && parseInt(reminderTime) > Date.now()) {
      setOpen(false);
    }
  }, []);

  const reloadUser = () => {
    setUserAtom(userAtom + 1);
  };

  const handleClose = () => setOpen(false);
  
  const handleRemindLater = () => {
    const reminderTime = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem(REMINDER_KEY, reminderTime.toString());
    setOpen(false);
  };

  const onSubmit = useRecoilCallback(
    ({ snapshot }) =>
      async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
        const payload = {
          ...profile,
          agreed_sms_date: new Date().toISOString(),
          agreed_sms: values.agreed_sms,
          send_receive_sms: values.agreed_sms
        };
        setLoading(true);
        setSubmitting(true);
        const res = await snapshot.getPromise(updateProfile(payload));
        setSubmitting(false);
        reloadUser();
        if (res.success) {
          setToast({
            message: 'Profile Updated Successfully!',
            type: 'success',
            open: true
          });
          handleClose();
        }
        setLoading(false);
      }
  );

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        open={toast.open}
        setToast={setToast}
      />
      {profileRequest.state === 'hasValue' && profile.agreed_sms === null && (
        <Dialog open={open} maxWidth={'sm'} fullWidth={true}>
          <DialogTitle disableTypography className={classes.dialogTitle}>
            <Typography variant="h4">SMS Consent</Typography>
          </DialogTitle>
          {loading && <LinearProgress />}
          <DialogContent className={classes.dialogContent}>
            <Formik
              initialValues={{
                agreed_sms: ''
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                handleChange,
                handleSubmit,
                isSubmitting,
                errors,
                touched
              }: any) => {
                return (
                  <Form onSubmit={handleSubmit} placeholder={''}>
                    <Grid item sm={12} xs={12} md={12} lg={12}>
                      <Typography variant="body2">
                        Do you consent to receive SMS notifications?
                      </Typography>
                      <RadioGroup
                        row
                        aria-label="agreed_sms"
                        name="agreed_sms"
                        value={values.agreed_sms}
                        onChange={handleChange}
                        style={{
                          color:
                            errors.agreed_sms && touched.agreed_sms
                              ? '#f44336'
                              : undefined
                        }}
                      >
                        <FormControlLabel
                          value="Y"
                          control={
                            <Radio
                              color={
                                errors.agreed_sms && touched.agreed_sms
                                  ? 'secondary'
                                  : 'primary'
                              }
                            />
                          }
                          label="Yes"
                        />
                        <FormControlLabel
                          value="N"
                          control={
                            <Radio
                              color={
                                errors.agreed_sms && touched.agreed_sms
                                  ? 'secondary'
                                  : 'primary'
                              }
                            />
                          }
                          label="No"
                        />
                      </RadioGroup>
                      {errors.agreed_sms && touched.agreed_sms && (
                        <Typography
                          color="error"
                          variant="caption"
                          style={{ marginTop: '4px' }}
                        >
                          {errors.agreed_sms}
                        </Typography>
                      )}
                    </Grid>
                    <Box mt={3} display="flex" justifyContent="space-between">
                      <Button
                        variant="outlined"
                        size="large"
                        color="primary"
                        onClick={handleRemindLater}
                        disabled={isSubmitting}
                      >
                        Remind me later
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting && (
                          <CircularProgress
                            className={marginClasses.mr2}
                            color="inherit"
                            size={25}
                          />
                        )}
                        Submit
                      </Button>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
